import type {
  ShopNumberingDto,
  UpdateShopNumberingDto,
} from '../../../../libs/api-client/src/generated/models';

/** The two document-number sequences a shop runs. */
export type NumberSequence = 'invoice' | 'creditNote';

/** What the operator has typed into the numbering form — strings, as the inputs hold them. */
export interface NumberingDraft {
  readonly invoicePrefix: string;
  readonly creditNotePrefix: string;
  readonly prependInvoiceYear: boolean;
  readonly nextInvoiceNo: string;
  readonly nextCreditNoteNo: string;
}

/** One change the confirm dialog spells out, with the consequence it carries. */
export type NumberingChange =
  | {
      readonly kind: 'counter';
      readonly sequence: NumberSequence;
      readonly fromNext: number;
      readonly toNext: number;
      /**
       * `raise`: `fromNext … toNext - 1` are skipped for good.
       * `lower`: `toNext … fromNext - 1` are handed out again.
       */
      readonly direction: 'raise' | 'lower';
      /**
       * Nothing issued in the sequence yet: any value goes, the start number moves with it, and
       * a lowering needs no acknowledgement — there is no document it could collide with.
       */
      readonly emptySequence: boolean;
    }
  | {
      readonly kind: 'prefix';
      readonly sequence: NumberSequence;
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: 'yearFlag'; readonly to: boolean };

/** Catalogue keys of the per-field validation messages. */
export type NumberingFieldErrors = Partial<
  Record<keyof NumberingDraft | 'reason', string>
>;

/** Longest prefix the API accepts — `DOCUMENT_PREFIX_MAX_LENGTH` in `apps/api/src/dto/shop.dto.ts`. */
export const PREFIX_MAX_LENGTH = 10;

/** The form's starting point: exactly the stored state, so an untouched form changes nothing. */
export function draftFrom(state: ShopNumberingDto): NumberingDraft {
  return {
    invoicePrefix: state.invoice.prefix ?? '',
    creditNotePrefix: state.creditNote.prefix ?? '',
    prependInvoiceYear: state.prependInvoiceYear,
    nextInvoiceNo: state.invoice.nextNo?.toString() ?? '',
    nextCreditNoteNo: state.creditNote.nextNo?.toString() ?? '',
  };
}

/** Whether the form differs from the stored state at all — typed garbage included. */
export function isDraftDirty(
  state: ShopNumberingDto,
  draft: NumberingDraft,
): boolean {
  const initial = draftFrom(state);
  return (
    draft.invoicePrefix !== initial.invoicePrefix ||
    draft.creditNotePrefix !== initial.creditNotePrefix ||
    draft.prependInvoiceYear !== initial.prependInvoiceYear ||
    draft.nextInvoiceNo !== initial.nextInvoiceNo ||
    draft.nextCreditNoteNo !== initial.nextCreditNoteNo
  );
}

/**
 * Whether a change hands out numbers again in a sequence that already has documents — the one
 * case the confirm dialog holds behind an explicit acknowledgement (as does the API).
 */
export function needsReuseAcknowledgement(change: NumberingChange): boolean {
  return (
    change.kind === 'counter' &&
    change.direction === 'lower' &&
    !change.emptySequence
  );
}

/**
 * A document number as it will be headed, for the live preview.
 *
 * A PREVIEW of `formatInvoiceNumberForShop` (libs/types), which the dashboard cannot import: the
 * server hands over today's year digits in the shop's timezone, so the one thing that is not
 * string concatenation is not re-derived here.
 */
export function previewNumber(
  prefix: string,
  prependYear: boolean,
  yearDigitsToday: string,
  value: number,
): string {
  return `${prefix}${prependYear ? yearDigitsToday : ''}${value}`;
}

/** Parse a counter input; `null` for anything that is not a positive whole number. */
export function parseNext(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/** Everything the draft would change, in the order the dialog lists it. */
export function diffNumbering(
  state: ShopNumberingDto,
  draft: NumberingDraft,
): NumberingChange[] {
  const changes: NumberingChange[] = [];

  const counters: Array<[NumberSequence, string]> = [
    ['invoice', draft.nextInvoiceNo],
    ['creditNote', draft.nextCreditNoteNo],
  ];
  for (const [sequence, raw] of counters) {
    const fromNext = state[sequence].nextNo;
    const toNext = parseNext(raw);
    if (fromNext == null || toNext == null || toNext === fromNext) continue;
    changes.push({
      kind: 'counter',
      sequence,
      fromNext,
      toNext,
      direction: toNext > fromNext ? 'raise' : 'lower',
      emptySequence: state[sequence].issuedCount === 0,
    });
  }

  const prefixes: Array<[NumberSequence, string]> = [
    ['invoice', draft.invoicePrefix],
    ['creditNote', draft.creditNotePrefix],
  ];
  for (const [sequence, to] of prefixes) {
    const from = state[sequence].prefix ?? '';
    if (to !== from) changes.push({ kind: 'prefix', sequence, from, to });
  }

  if (draft.prependInvoiceYear !== state.prependInvoiceYear) {
    changes.push({ kind: 'yearFlag', to: draft.prependInvoiceYear });
  }

  return changes;
}

/**
 * Client-side mirror of the API's rules, so the operator hears about a bad value at the field
 * rather than as a 409 after the confirm. The server stays the authority — it re-checks all of
 * this under a row lock.
 */
export function validateNumberingDraft(
  state: ShopNumberingDto,
  draft: NumberingDraft,
  reason: string,
): NumberingFieldErrors {
  const errors: NumberingFieldErrors = {};

  const counters: Array<
    [NumberSequence, 'nextInvoiceNo' | 'nextCreditNoteNo']
  > = [
    ['invoice', 'nextInvoiceNo'],
    ['creditNote', 'nextCreditNoteNo'],
  ];
  for (const [sequence, field] of counters) {
    if (!state[sequence].configured) continue;
    const value = parseNext(draft[field]);
    if (value == null) {
      errors[field] = 'numbering.errorNextInvalid';
    } else if (value < state[sequence].minNextNo) {
      errors[field] = 'numbering.errorNextTooLow';
    }
  }

  const prefixes: Array<
    ['invoicePrefix' | 'creditNotePrefix', NumberSequence]
  > = [
    ['invoicePrefix', 'invoice'],
    ['creditNotePrefix', 'creditNote'],
  ];
  for (const [field, sequence] of prefixes) {
    const value = draft[field];
    if (value === (state[sequence].prefix ?? '')) continue;
    if (value.length === 0) {
      errors[field] = 'numbering.errorPrefixEmpty';
    } else if (value.length > PREFIX_MAX_LENGTH || /[\r\n]/.test(value)) {
      errors[field] = 'numbering.errorPrefixInvalid';
    }
  }
  const prefixTouched =
    draft.invoicePrefix !== (state.invoice.prefix ?? '') ||
    draft.creditNotePrefix !== (state.creditNote.prefix ?? '');
  if (
    prefixTouched &&
    !errors.creditNotePrefix &&
    draft.creditNotePrefix === draft.invoicePrefix
  ) {
    errors.creditNotePrefix = 'numbering.errorPrefixSame';
  }

  if (reason.trim().length < 10) {
    errors.reason = 'common.reasonMin';
  } else if (reason.length > 500) {
    errors.reason = 'common.reasonMax';
  }

  return errors;
}

/** The request body for the changes in `changes` — only the fields that actually move. */
export function toUpdatePayload(
  state: ShopNumberingDto,
  changes: readonly NumberingChange[],
  reason: string,
): UpdateShopNumberingDto {
  const payload: UpdateShopNumberingDto = {
    expectedCurrentInvoiceNo: state.invoice.currentNo,
    expectedCurrentCreditNoteNo: state.creditNote.currentNo,
    reason: reason.trim(),
  };
  for (const change of changes) {
    if (change.kind === 'counter') {
      if (change.sequence === 'invoice') payload.nextInvoiceNo = change.toNext;
      else payload.nextCreditNoteNo = change.toNext;
      if (needsReuseAcknowledgement(change)) {
        payload.acknowledgeNumberReuse = true;
      }
    } else if (change.kind === 'prefix') {
      if (change.sequence === 'invoice') payload.invoicePrefix = change.to;
      else payload.creditNotePrefix = change.to;
    } else {
      payload.prependInvoiceYear = change.to;
    }
  }
  return payload;
}
